import { Injectable, Signal, computed, signal } from '@angular/core';
import { FirebaseApp, FirebaseError, getApps, initializeApp } from 'firebase/app';
import * as remoteConfigSdk from 'firebase/remote-config';
import { RemoteConfig } from 'firebase/remote-config';

import { environment } from '../../../environments/environment';

export const UNDER_CONSTRUCTION_CONFIG_KEY = 'under_construction';

type RemoteConfigDefaultValue = string | number | boolean;

const REMOTE_CONFIG_APP_NAME = 'karting-stats-remote-config';
const REMOTE_CONFIG_DEFAULTS: Record<string, RemoteConfigDefaultValue> = {
  [UNDER_CONSTRUCTION_CONFIG_KEY]: environment.underConstruction,
};

interface RemoteConfigRealtimeUpdate {
  getUpdatedKeys(): Iterable<string>;
}

interface RemoteConfigRealtimeObserver {
  next: (configUpdate: RemoteConfigRealtimeUpdate) => void | Promise<void>;
  error?: (err: FirebaseError) => void;
  complete?: () => void;
}

type RemoteConfigSdkWithRealtime = typeof remoteConfigSdk & {
  onConfigUpdate?: (remoteConfig: RemoteConfig, observer: RemoteConfigRealtimeObserver) => () => void;
};

@Injectable({
  providedIn: 'root',
})
export class RemoteConfigService {
  private firebaseApp: FirebaseApp;
  private remoteConfig: RemoteConfig;
  private defaults: Record<string, RemoteConfigDefaultValue> = { ...REMOTE_CONFIG_DEFAULTS };
  private trackedKeys = new Set<string>(Object.keys(REMOTE_CONFIG_DEFAULTS));
  private rawValues = signal<Record<string, string>>(this.serializeDefaults(REMOTE_CONFIG_DEFAULTS));
  private stringSignals = new Map<string, Signal<string | null>>();
  private numberSignals = new Map<string, Signal<number | null>>();
  private booleanSignals = new Map<string, Signal<boolean | null>>();
  private initializingPromise: Promise<void> | null = null;
  private unsubscribeRealtimeUpdates: (() => void) | null = null;
  private initialized = false;

  public readonly ready = signal(false);

  constructor() {
    this.firebaseApp = this.getOrCreateRemoteConfigApp();
    this.remoteConfig = remoteConfigSdk.getRemoteConfig(this.firebaseApp);
    this.remoteConfig.defaultConfig = { ...this.defaults };
    this.remoteConfig.settings = {
      // Production uses Firebase's 12-hour default so page loads reuse cache instead of
      // billing a fetch each time. Local sets this to 60s so flag changes are easy to test.
      minimumFetchIntervalMillis: environment.fastRemoteConfigFetch ? 60 * 1000 : 12 * 60 * 60 * 1000,
      fetchTimeoutMillis: 10 * 1000,
    };
    this.syncKeys(this.trackedKeys);
  }

  /**
   * Prime the local cache and start real-time updates when supported by the SDK.
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      this.startRealtimeUpdates();
      return;
    }

    if (!this.initializingPromise) {
      this.initializingPromise = this.initializeInternal().finally(() => {
        this.initializingPromise = null;
      });
    }

    await this.initializingPromise;
  }

  public numberSignal(remoteConfigKey: string, fallback: number | null = null): Signal<number | null> {
    this.registerKey(remoteConfigKey, fallback ?? undefined);

    const existingSignal = this.numberSignals.get(remoteConfigKey);
    if (existingSignal) {
      return existingSignal;
    }

    const numberSignal = computed(() => this.readNumberValue(remoteConfigKey, fallback));
    this.numberSignals.set(remoteConfigKey, numberSignal);
    return numberSignal;
  }

  public booleanSignal(remoteConfigKey: string, fallback: boolean | null = null): Signal<boolean | null> {
    this.registerKey(remoteConfigKey, fallback ?? undefined);

    const existingSignal = this.booleanSignals.get(remoteConfigKey);
    if (existingSignal) {
      return existingSignal;
    }

    const booleanSignal = computed(() => this.readBooleanValue(remoteConfigKey, fallback));
    this.booleanSignals.set(remoteConfigKey, booleanSignal);
    return booleanSignal;
  }

  public stringSignal(remoteConfigKey: string, fallback: string | null = null): Signal<string | null> {
    this.registerKey(remoteConfigKey, fallback ?? undefined);

    const existingSignal = this.stringSignals.get(remoteConfigKey);
    if (existingSignal) {
      return existingSignal;
    }

    const stringSignal = computed(() => this.readStringValue(remoteConfigKey, fallback));
    this.stringSignals.set(remoteConfigKey, stringSignal);
    return stringSignal;
  }

  public async getRemoteConfig(remoteConfigKey: string, fallback: number | null = null): Promise<number | null> {
    await this.initialize();
    return this.readNumberValue(remoteConfigKey, fallback);
  }

  public async getRemoteConfigString(remoteConfigKey: string, fallback: string | null = null): Promise<string | null> {
    await this.initialize();
    return this.readStringValue(remoteConfigKey, fallback);
  }

  public async getRemoteConfigBoolean(remoteConfigKey: string, fallback: boolean | null = null): Promise<boolean | null> {
    await this.initialize();
    return this.readBooleanValue(remoteConfigKey, fallback);
  }

  private async initializeInternal(): Promise<void> {
    try {
      await remoteConfigSdk.fetchAndActivate(this.remoteConfig);
    } catch (err) {
      console.error('Error fetching remote config: ', err);
    }

    this.syncKeys(this.trackedKeys);
    this.startRealtimeUpdates();
    this.initialized = true;
    this.ready.set(true);
  }

  private startRealtimeUpdates(): void {
    if (this.unsubscribeRealtimeUpdates) {
      return;
    }

    const realtimeSdk = remoteConfigSdk as RemoteConfigSdkWithRealtime;
    if (!realtimeSdk.onConfigUpdate) {
      return;
    }

    this.unsubscribeRealtimeUpdates = realtimeSdk.onConfigUpdate(this.remoteConfig, {
      next: async (configUpdate: RemoteConfigRealtimeUpdate) => {
        try {
          await remoteConfigSdk.activate(this.remoteConfig);
          this.syncKeys(configUpdate.getUpdatedKeys());
        } catch (err) {
          console.error('Error activating remote config update: ', err);
        }
      },
      error: (err: FirebaseError) => {
        this.unsubscribeRealtimeUpdates = null;
        console.error('Error listening for remote config updates: ', err);
      },
      complete: () => {
        this.unsubscribeRealtimeUpdates = null;
      },
    });
  }

  private registerKey(remoteConfigKey: string, fallback?: RemoteConfigDefaultValue): void {
    this.trackedKeys.add(remoteConfigKey);

    if (fallback !== undefined && !(remoteConfigKey in this.defaults)) {
      this.defaults[remoteConfigKey] = fallback;
      this.remoteConfig.defaultConfig = { ...this.defaults };
      this.rawValues.update((currentValues) => ({
        ...currentValues,
        [remoteConfigKey]: this.serializeDefaultValue(fallback),
      }));
    }

    this.syncKeys([remoteConfigKey]);
  }

  private syncKeys(keys: Iterable<string>): void {
    this.rawValues.update((currentValues) => {
      const nextValues = { ...currentValues };

      for (const key of keys) {
        nextValues[key] = remoteConfigSdk.getValue(this.remoteConfig, key).asString();
      }

      return nextValues;
    });
  }

  private readStringValue(remoteConfigKey: string, fallback: string | null = null): string | null {
    const value = this.rawValues()[remoteConfigKey];

    if (value !== undefined) {
      return value;
    }

    return fallback;
  }

  private readNumberValue(remoteConfigKey: string, fallback: number | null = null): number | null {
    const value = this.readStringValue(remoteConfigKey, null);

    if (value === null || value.trim() === '') {
      return fallback;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? fallback : parsedValue;
  }

  private readBooleanValue(remoteConfigKey: string, fallback: boolean | null = null): boolean | null {
    const value = this.readStringValue(remoteConfigKey, null);

    if (value === null) {
      return fallback;
    }

    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        return fallback;
    }
  }

  private getOrCreateRemoteConfigApp(): FirebaseApp {
    const existingApp = getApps().find((app) => app.name === REMOTE_CONFIG_APP_NAME);

    if (existingApp) {
      return existingApp;
    }

    return initializeApp(environment.firebaseConfig, REMOTE_CONFIG_APP_NAME);
  }

  private serializeDefaults(defaults: Record<string, RemoteConfigDefaultValue>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(defaults).map(([key, value]) => [key, this.serializeDefaultValue(value)]),
    );
  }

  private serializeDefaultValue(value: RemoteConfigDefaultValue): string {
    return String(value);
  }
}
